import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, FlaskConical, KeyRound, X } from "lucide-react";
import { Button } from "./Button";
import { useAuth } from "../hooks/useAuth";
import { redeemKey } from "../api/billing";
import { submitFeedback } from "../api/feedback";
import { useBetaGate } from "../hooks/useFeatureFlags";

// TEMPORARY beta gate: while the `beta_gate` runtime flag is on (admin >
// Flags, no redeploy needed), every Stripe entry point on the web app (Pro
// checkout, supporters bundle, store card/gift checkout) opens this modal
// instead of creating a checkout session, and the API refuses checkout too.
// Beta testers redeem an admin-generated plan key ("beta key"); everyone else
// can request access via a short form that lands in the feedback inbox (and is
// mirrored to Linear), where the submitter's account email is already attached.
// Read the flag with useBetaGate() from hooks/useFeatureFlags.

type View = "gate" | "form" | "formDone" | "redeemed";

// Slim strip pinned above the page content in AppShell while the beta gate is
// on — same kill switch as the modal.
const BANNER_DISMISSED_KEY = "atlas-beta-banner-dismissed";

export function BetaBanner({ onSendFeedback }: { onSendFeedback?: () => void }) {
  const betaGate = useBetaGate();
  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem(BANNER_DISMISSED_KEY) === "1",
  );
  if (!betaGate || dismissed) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
        padding: "7px 16px",
        background: "var(--info-soft)",
        borderBottom: "1px solid var(--border-subtle)",
        fontSize: 12.5,
        lineHeight: 1.4,
        color: "var(--text-secondary)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <FlaskConical size={13} style={{ color: "var(--status-info)", flexShrink: 0 }} />
      <span style={{ minWidth: 0 }}>
        Atlas is in beta — things are still being improved and may change.
      </span>
      {onSendFeedback && (
        <button
          type="button"
          onClick={onSendFeedback}
          style={{
            marginLeft: "auto",
            flexShrink: 0,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: "var(--font-sans)",
            color: "var(--status-info)",
            textDecoration: "underline",
            textUnderlineOffset: 2,
          }}
        >
          Send feedback
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          sessionStorage.setItem(BANNER_DISMISSED_KEY, "1");
          setDismissed(true);
        }}
        aria-label="Dismiss beta banner"
        style={{
          marginLeft: onSendFeedback ? 0 : "auto",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          background: "none",
          border: "none",
          padding: 2,
          cursor: "pointer",
          color: "var(--text-secondary)",
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: "var(--control-md)",
  padding: "0 12px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: "var(--text-base)",
  fontFamily: "var(--font-sans)",
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  height: "auto",
  minHeight: 76,
  padding: "10px 12px",
  resize: "vertical",
  lineHeight: 1.5,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text-secondary)",
  marginBottom: 6,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

export function BetaGateModal({
  onClose,
  onRedeemedDone,
}: {
  onClose: () => void;
  /** Called instead of onClose when the user clicks Done after redeeming a key. */
  onRedeemedDone?: () => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [view, setView] = React.useState<View>("gate");
  const [keyInput, setKeyInput] = React.useState("");
  const [redeemedPlan, setRedeemedPlan] = React.useState<{ plan: string; expiresAt: string | null } | null>(null);

  const [referred, setReferred] = React.useState<"yes" | "no">("no");
  const [referredBy, setReferredBy] = React.useState("");
  const [howFound, setHowFound] = React.useState("");
  const [interests, setInterests] = React.useState("");

  const redeemMutation = useMutation({
    mutationFn: (code: string) => redeemKey(code),
    onSuccess: (data) => {
      setRedeemedPlan({ plan: data.plan, expiresAt: data.expiresAt });
      setView("redeemed");
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  const requestMutation = useMutation({
    mutationFn: () =>
      submitFeedback({
        type: "other",
        title: "Beta access request",
        body:
          referred === "yes"
            ? `Referred by: ${referredBy.trim()}`
            : `How they found Atlas: ${howFound.trim()}\n\nWhat interests them: ${interests.trim()}`,
        details: {
          request: "beta-access",
          referred,
          ...(referred === "yes"
            ? { referredBy: referredBy.trim() }
            : { howFound: howFound.trim(), interests: interests.trim() }),
        },
        context: {
          url: window.location.href,
          userAgent: navigator.userAgent,
          plan: user?.plan ?? null,
        },
      }),
    onSuccess: () => setView("formDone"),
  });

  const canSubmitRequest =
    !requestMutation.isPending &&
    (referred === "yes"
      ? referredBy.trim().length > 0
      : howFound.trim().length > 0 && interests.trim().length > 0);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--scrim)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100, // above the store's item detail modal (1000)
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 460,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        {view === "gate" && (
          <>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "var(--radius-md)",
                background: "var(--accent-soft)",
                border: "1px solid var(--border-gold)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--gold-400)",
                marginBottom: 14,
              }}
            >
              <FlaskConical size={20} />
            </div>
            <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
              Atlas is in beta
            </h2>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.55 }}>
              We're beta testing right now and aren't taking payments — we want the product in
              great shape before we accept your money. During the beta, Pro access is unlocked
              with a beta key instead.
            </p>

            <Field label="Redeem a beta key">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (keyInput.trim()) redeemMutation.mutate(keyInput.trim());
                }}
                style={{ display: "flex", gap: 8 }}
              >
                <input
                  type="text"
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value.toUpperCase())}
                  autoFocus
                  style={{
                    ...inputStyle,
                    flex: 1,
                    width: "auto",
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    letterSpacing: "0.05em",
                  }}
                />
                <Button
                  type="submit"
                  variant="primary"
                  leftIcon={<KeyRound size={14} />}
                  disabled={!keyInput.trim() || redeemMutation.isPending}
                >
                  {redeemMutation.isPending ? "Redeeming…" : "Redeem"}
                </Button>
              </form>
            </Field>
            {redeemMutation.isError && (
              <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: -6, marginBottom: 14 }}>
                {errorMessage(redeemMutation.error, "Redemption failed")}
              </p>
            )}

            <div
              style={{
                borderTop: "1px solid var(--border-subtle)",
                marginTop: 6,
                paddingTop: 16,
              }}
            >
              <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12, lineHeight: 1.55 }}>
                No key yet? If you'd like to be involved in beta testing, tell us a little about
                yourself and we'll be in touch.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <Button variant="ghost" onClick={onClose}>Close</Button>
                <Button variant="secondary" onClick={() => setView("form")}>
                  Request beta access
                </Button>
              </div>
            </div>
          </>
        )}

        {view === "form" && (
          <>
            <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
              Request beta access
            </h2>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 18, lineHeight: 1.55 }}>
              A couple of quick questions. If we invite you, we'll email a beta key to the
              address on your account.
            </p>

            <Field label="Were you referred by someone?">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {(["yes", "no"] as const).map((opt) => {
                  const active = referred === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setReferred(opt)}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "var(--radius-md)",
                        background: active ? "var(--accent-soft)" : "var(--surface-sunken)",
                        border: `1px solid ${active ? "var(--border-gold)" : "var(--border-subtle)"}`,
                        color: active ? "var(--text-primary)" : "var(--text-secondary)",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: "var(--font-sans)",
                      }}
                    >
                      {opt === "yes" ? "Yes" : "No"}
                    </button>
                  );
                })}
              </div>
            </Field>

            {referred === "yes" ? (
              <Field label="Who referred you?">
                <input
                  type="text"
                  value={referredBy}
                  onChange={(e) => setReferredBy(e.target.value)}
                  placeholder="Their name or username"
                  style={inputStyle}
                />
              </Field>
            ) : (
              <>
                <Field label="How did you find Atlas?">
                  <input
                    type="text"
                    value={howFound}
                    onChange={(e) => setHowFound(e.target.value)}
                    placeholder="e.g. a friend, Reddit, search"
                    style={inputStyle}
                  />
                </Field>
                <Field label="What interests you about Atlas?">
                  <textarea
                    value={interests}
                    onChange={(e) => setInterests(e.target.value)}
                    placeholder="What would you use it for?"
                    style={textareaStyle}
                  />
                </Field>
              </>
            )}

            {requestMutation.isError && (
              <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>
                {errorMessage(requestMutation.error, "Failed to send request")}
              </p>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
              <Button variant="ghost" onClick={() => setView("gate")}>Back</Button>
              <Button variant="primary" disabled={!canSubmitRequest} onClick={() => requestMutation.mutate()}>
                {requestMutation.isPending ? "Sending…" : "Submit request"}
              </Button>
            </div>
          </>
        )}

        {view === "formDone" && (
          <>
            <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
              Request received
            </h2>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.55 }}>
              Thanks — we review every request. If we invite you to the beta, we'll email a key
              to the address on your account.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="primary" onClick={onClose}>Done</Button>
            </div>
          </>
        )}

        {view === "redeemed" && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                background: "var(--accent-soft)",
                border: "1px solid var(--border-gold)",
                borderRadius: "var(--radius-md)",
                fontSize: 13,
                color: "var(--text-accent)",
                marginBottom: 16,
              }}
            >
              <Check size={14} style={{ flexShrink: 0 }} />
              Key redeemed — your plan is now {redeemedPlan?.plan ?? "pro"}
              {redeemedPlan?.expiresAt
                ? ` until ${new Date(redeemedPlan.expiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                : ""}
              .
            </div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.55 }}>
              Welcome to the beta. Atlas will start matching your older activities shortly.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="primary" onClick={onRedeemedDone ?? onClose}>Done</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
